import { post } from 'app/shared/utils/fetchr';
import { useQuery } from '@tanstack/react-query';
import { ALERT_QUERY_KEYS } from 'app/modules/alerts/queries/keys';
import { ShortActionEventResponse } from 'app/modules/actionEvents/responses';
import { PaginationPayload } from 'app/shared/pagination/models';
import { useSelector } from 'react-redux';
import { selectHasReadAlertsPermission } from 'app/modules/session/selectors';
import { selectDLEnabledForAlertActionEvents } from 'app/shared/featureFlags/selectors';
import { useEffect, useState } from 'react';
import { ColdStorageLoadingStatus } from 'app/shared/utils/fastArchHelpers';
import { useGetAlert } from 'app/modules/alerts/queries/useGetAlert';

const fetchAlertActionEvents = (
  id: number,
  body: PaginationPayload,
): Promise<{ count: number | null; events: ShortActionEventResponse[] }> =>
  post(`/v1/alerts/${id}/action-events`, body);

export const fetchAlertActionEventsFromCache = (
  id: number,
  body: PaginationPayload,
): Promise<{ count: number | null; events: ShortActionEventResponse[] }> => {
  return post(`/v1/alerts/cached/${id}/action-events`, body);
};

export const useGetAlertActionEvents = (
  id: number,
  body: PaginationPayload,
  enabled: boolean = true,
) => {
  const [prevColdStorageLoadingStatus, setPrevColdStorageLoadingStatus] =
    useState<ColdStorageLoadingStatus | null>(null);
  const useCacheEndpoint = useSelector(selectDLEnabledForAlertActionEvents);
  const { coldStorageLoading } = useGetAlert(id, {
    enabled: useCacheEndpoint,
  });
  const hasReadAlertsPermission = useSelector(selectHasReadAlertsPermission);
  const queryEnabled = hasReadAlertsPermission && body.limit !== 0 && id !== -1;

  const query = useQuery({
    queryKey: ALERT_QUERY_KEYS.getAlertActionEvents(id, body, useCacheEndpoint),
    queryFn: () =>
      useCacheEndpoint
        ? fetchAlertActionEventsFromCache(id, body)
        : fetchAlertActionEvents(id, body),
    meta: { errorMessage: 'Failed to load alert action events.' },
    enabled: queryEnabled && enabled,
    staleTime: 1000,
  });

  const { refetch } = query;
  useEffect(() => {
    // Re fetch action events if we were waiting for the action events to load from cold storage
    const wasLoading =
      prevColdStorageLoadingStatus !== null &&
      prevColdStorageLoadingStatus !== ColdStorageLoadingStatus.LOADED;
    if (
      queryEnabled &&
      useCacheEndpoint &&
      wasLoading &&
      coldStorageLoading.action_event === ColdStorageLoadingStatus.LOADED
    ) {
      refetch();
    }
    setPrevColdStorageLoadingStatus(coldStorageLoading.action_event);
  }, [
    coldStorageLoading.action_event,
    prevColdStorageLoadingStatus,
    useCacheEndpoint,
    queryEnabled,
    refetch,
  ]);

  return query;
};
